import { observer } from 'mobx-react';
import * as React from 'react';
import {
  Button,
  Card,
  ErrorMessage,
  FlexRow,
  Footer,
  FormCell,
  Grid,
  WorkflowHeader,
  Icon,
  IconSize,
  IconType,
} from 'ui-toolkit';
import { IPolicyJacketUI } from '../classes/PolicyJacketUI';
import { IPolicyOrderDetails } from '../classes/PolicyOrderDetails';
import { IRecordedDeed } from '../classes/RecordedDeed';
import FileUploadButton from '../components/fields/FileUploadButton/FileUploadButton';
import FileReview from '../components/FileReview';
import PolicyScheduleAPreview from '../components/PolicyScheduleAPreview';
import PreviewButtonBlock from '../components/PreviewButtonBlock';
import getText from '../utils/getText';
import { IRecordedMortgage } from '../classes/RecordedMortgage';
import { IPolicyNumberError } from '../classes/IPolicyNumberError';
import { PDFPreview } from 'ui-toolkit/components/PDFPreview';
import { RouteComponentProps } from 'react-router';
import IRouterParams from '../IRouterParams';
import { clientUrls } from '../../ui-toolkit/rest/ClientUrlFactory';

interface IPolicyReview extends RouteComponentProps<IRouterParams> {
  orderDetails: IPolicyOrderDetails;
  title: string;
  showEndorsements: boolean;
  showMortgageeClause: boolean;
  onBackClick: () => void;
  onFinalizeClick: () => void;
  onJacketUpload: (files: FileList | null) => void;
  finalizeInProcess: boolean;
  finalizedSuccess: boolean;
  onDeletePolicyJacket: () => void;
  getPolicyJacket: IPolicyJacketUI;
  recordedDeed: IRecordedDeed;
  getEndorsements: string[];
  pageErrorMessage: string | IPolicyNumberError;
  policyJacketError: any;
  editAddressUrl: string;
  editAmtOfInsuredURL: string;
  editBorrowerPage: string;
  cdBuilderPage: string;
  showUpdateSpinner: boolean;
  showUpdateSuccess: boolean;
  onChangePolicyNumber: (newValue: string | null, error?: string | null) => void;
  onUpdatePageClick: () => void;
  onPolicyNumberBlur: () => void;
  pageLoading: boolean;
  policyUpdateFetching: boolean;
  policyUpdateSuccess: boolean;
  policyJacketFetching: boolean;
  policyJacketSuccess: boolean;
  disableFinalizeButton: boolean;
  isAmericanDigital: boolean;
  recordedMortgage: IRecordedMortgage;
}

interface IState {
  showPdfPreview: boolean;
}

class PolicyReview extends React.Component<IPolicyReview, IState> {
  public readonly state: IState = {
    showPdfPreview: false,
  };

  private getErrorMessage(message: string | IPolicyNumberError) {
    if (typeof message === 'string') {
      return message;
    }
    return message?.errorMessage;
  }

  public render() {
    return (
      <div className="App">
        <div>
          <WorkflowHeader title={this.props.title} onHomeClick={this.goToHomePage} onBackClick={this.goBack}>
            <PreviewButtonBlock
              finalizedSuccess={this.props.finalizedSuccess}
              disableFinalizeButton={this.props.disableFinalizeButton}
              onFinalizeClick={this.props.onFinalizeClick}
            />
          </WorkflowHeader>
          <div className={'page-content'}>
            {this.props.pageErrorMessage && (
              <ErrorMessage message={this.getErrorMessage(this.props.pageErrorMessage)} />
            )}
            <Card>
              <Grid>
                <FormCell label={'Step 3:'}>
                  <FlexRow>
                    <h2>Review Policy Details</h2>
                  </FlexRow>
                </FormCell>
                <Grid>
                  <FormCell label={getText(`policy.policyJacketLabel.${this.props.match.params.policyType}`)}>
                    <FlexRow>
                      {this.props.policyJacketSuccess ? (
                        <Icon type={IconType.Check} size={IconSize.Small} />
                      ) : this.props.getPolicyJacket.jacketName && !this.props.policyJacketFetching ? (
                        <FileReview
                          onDeleteClick={this.props.onDeletePolicyJacket}
                          onFileNameClick={this.togglePdfPreview}
                          fileName={this.props.getPolicyJacket.jacketName}
                        />
                      ) : (
                        <div>
                          <FileUploadButton onFileUpload={this.props.onJacketUpload} />
                        </div>
                      )}
                    </FlexRow>
                  </FormCell>
                </Grid>
                <PolicyScheduleAPreview
                  orderDetails={this.props.orderDetails}
                  getPolicyJacket={this.props.getPolicyJacket}
                  policyJacketError={this.props.policyJacketError}
                  recordedDeed={this.props.recordedDeed}
                  getEndorsements={this.props.getEndorsements}
                  showEndorsements={this.props.showEndorsements}
                  showMortgageeClause={this.props.showMortgageeClause}
                  editAddressUrl={this.props.editAddressUrl}
                  editAmtOfInsuredURL={this.props.editAmtOfInsuredURL}
                  editBorrowerPage={this.props.editBorrowerPage}
                  cdBuilderPage={this.props.cdBuilderPage}
                  showUpdateSpinner={this.props.showUpdateSpinner}
                  showUpdateSuccess={this.props.showUpdateSuccess}
                  policyUpdatedSuccess={this.props.policyUpdateSuccess}
                  policyUpdateFetching={this.props.policyUpdateFetching}
                  pageContainsError={!!this.props.pageErrorMessage}
                  policyType={this.props.match.params.policyType}
                  onPolicyNumberBlur={this.props.onPolicyNumberBlur}
                  onUpdatePageClick={this.props.onUpdatePageClick}
                  onChangePolicyNumber={this.props.onChangePolicyNumber}
                  isAmericanDigital={this.props.isAmericanDigital}
                  recordedMortgage={this.props.recordedMortgage}
                />
              </Grid>
            </Card>
          </div>
        </div>
        <Footer>
          <FlexRow justifyContent={'space-between'} width={100}>
            <FlexRow>
              <Button intent="primary" onClick={this.props.onBackClick}>
                Back
              </Button>
              <span>Back: Schedule B</span>
            </FlexRow>
            <PreviewButtonBlock
              finalizedSuccess={this.props.finalizedSuccess}
              disableFinalizeButton={this.props.disableFinalizeButton}
              onFinalizeClick={this.props.onFinalizeClick}
            />
          </FlexRow>
        </Footer>

        {this.state.showPdfPreview &&
        (this.props.getPolicyJacket.file || this.props.getPolicyJacket.latestJacketLink) ? (
          <PDFPreview
            file={
              this.props.getPolicyJacket.latestJacketLink
                ? this.props.getPolicyJacket.latestJacketLink
                : this.props.getPolicyJacket.file
            }
            onClose={this.togglePdfPreview}
          />
        ) : null}
      </div>
    );
  }

  private goToHomePage = () => {
    this.props.history.push(clientUrls.urlFor.adminOrders());
  };

  private goBack = () => {
    this.props.history.push(clientUrls.urlFor.title(this.props.match.params.orderId));
  };

  private togglePdfPreview = () => {
    this.setState((state) => ({
      showPdfPreview: !state.showPdfPreview,
    }));
  };
}

export default observer(PolicyReview);
